import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  SegmentedControl,
  LoadingOverlay,
  Stack,
  Title,
  Grid,
  Center,
  ThemeIcon,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconWifi,
  IconTrophy,
  IconBattery4,
  IconSignal4g,
  IconCheck,
} from "@tabler/icons-react";
import { API_BASE_URL } from "../../config/baseUrls";

export default function MegaQuizPreview() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [megaQuiz, setMegaQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [language, setLanguage] = useState("en");
  const [viewMode, setViewMode] = useState("preview");

  // --- LOGIC (UNTOUCHED) ---
  useEffect(() => {
    fetchMegaQuizData();
  }, [id]);

  const fetchMegaQuizData = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(
        `${API_BASE_URL}/admin/mega-quizzes/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const result = await res.json();
      if (result.success) {
        const quizData = result.data;
        setMegaQuiz({
          ...quizData,
          questions: quizData.questions.map((q) => ({
            ...q,
            correctAnswerIndex: q.options.findIndex(
              (opt) => opt.megaOptionId === q.answerId,
            ),
          })),
        });
      } else setError("Failed");
    } catch (e) {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (qIdx, oIdx) =>
    setSelectedAnswers((p) => ({ ...p, [qIdx]: oIdx }));
  const getScore = () => {
    let c = 0;
    megaQuiz.questions.forEach((q, i) => {
      if (selectedAnswers[i] === q.correctAnswerIndex) c++;
    });
    return c;
  };
  // --- LOGIC ENDS ---

  if (loading) return <LoadingOverlay visible />;
  if (!megaQuiz) return <Text>Not Found</Text>;

  const currentQ = megaQuiz.questions[currentQuestion];

  return (
    <Box className="h-full">
      {/* Header Controls */}
      <Group justify="space-between" mb="lg">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin/dashboard/mega-quiz-management")}
        >
          Back
        </Button>
        <SegmentedControl
          value={viewMode}
          onChange={setViewMode}
          data={[
            { label: "Mobile Preview", value: "preview" },
            { label: "Admin Data", value: "admin" },
          ]}
        />
      </Group>

      {viewMode === "admin" ? (
        <Paper p="xl" withBorder radius="md">
          <Title order={2} mb="md">
            {megaQuiz.titleEn}
          </Title>
          <Grid>
            <Grid.Col span={3}>
              <Stack
                gap={0}
                align="center"
                bg="gray.0"
                p="md"
                style={{ borderRadius: 8 }}
              >
                <Text fw={700} size="xl" c="blue">
                  {megaQuiz.questions.length}
                </Text>
                <Text size="xs" c="dimmed">
                  Questions
                </Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack
                gap={0}
                align="center"
                bg="gray.0"
                p="md"
                style={{ borderRadius: 8 }}
              >
                <Text fw={700} size="xl" c="purple">
                  {megaQuiz._count?.rankings || 0}
                </Text>
                <Text size="xs" c="dimmed">
                  Players
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Paper>
      ) : (
        /* PHONE SIMULATOR */
        <Center>
          <Box
            w={360}
            h={720}
            bg="black"
            style={{
              borderRadius: 48,
              border: "12px solid #222",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              className="absolute top-0 w-full h-full bg-gray-50"
              style={{ overflowY: "auto" }}
            >
              {/* Status Bar */}
              <Group justify="space-between" px="lg" pt="md" mb="md">
                <Text size="xs" fw={700}>
                  9:41
                </Text>
                <Group gap={4}>
                  <IconSignal4g size={12} />
                  <IconBattery4 size={14} />
                </Group>
              </Group>

              {/* App Content */}
              <Stack p="md" gap="lg">
                {/* Language Toggle Inside Phone */}
                <SegmentedControl
                  fullWidth
                  size="xs"
                  value={language}
                  onChange={setLanguage}
                  data={["en", "ja"]}
                />

                {!showResults ? (
                  <>
                    {/* Progress */}
                    <Box>
                      <Group justify="space-between" mb={4}>
                        <Text size="xs" fw={700} c="dimmed">
                          Q {currentQuestion + 1} / {megaQuiz.questions.length}
                        </Text>
                        <Badge color="purple" variant="dot">
                          Mega Quiz
                        </Badge>
                      </Group>
                      <div className="w-full h-1 bg-gray-200 rounded-full">
                        <div
                          className="h-full bg-purple-600 transition-all"
                          style={{
                            width: `${(currentQuestion / megaQuiz.questions.length) * 100}%`,
                          }}
                        />
                      </div>
                    </Box>

                    {/* Question Card */}
                    <Paper p="lg" radius="lg" shadow="sm" withBorder>
                      {currentQ.mediaUrl && (
                        <Box
                          mb="md"
                          style={{ borderRadius: 8, overflow: "hidden" }}
                        >
                          <img
                            src={currentQ.mediaUrl}
                            alt="Q"
                            style={{ width: "100%" }}
                          />
                        </Box>
                      )}
                      <Text fw={600} size="lg" style={{ lineHeight: 1.3 }}>
                        {language === "en"
                          ? currentQ.questionEn
                          : currentQ.questionJa}
                      </Text>
                    </Paper>

                    {/* Options */}
                    <Stack gap="sm">
                      {currentQ.options.map((opt, idx) => (
                        <Paper
                          key={idx}
                          p="md"
                          radius="md"
                          withBorder
                          onClick={() => handleAnswer(currentQuestion, idx)}
                          className={`cursor-pointer transition-all ${
                            selectedAnswers[currentQuestion] === idx
                              ? "bg-purple-50 border-purple-500 ring-1 ring-purple-500"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <Group>
                            <Box
                              className={`w-6 h-6 rounded-full border flex items-center justify-center ${selectedAnswers[currentQuestion] === idx ? "bg-purple-600 border-purple-600 text-white" : "border-gray-400"}`}
                            >
                              {selectedAnswers[currentQuestion] === idx && (
                                <IconCheck size={12} />
                              )}
                            </Box>
                            <Text size="sm" fw={500}>
                              {language === "en" ? opt.optionEn : opt.optionJa}
                            </Text>
                          </Group>
                        </Paper>
                      ))}
                    </Stack>

                    {/* Nav */}
                    <Group mt="auto">
                      <Button
                        variant="default"
                        disabled={currentQuestion === 0}
                        onClick={() => setCurrentQuestion((c) => c - 1)}
                        flex={1}
                      >
                        Prev
                      </Button>
                      <Button
                        color="purple"
                        onClick={() => {
                          if (currentQuestion < megaQuiz.questions.length - 1)
                            setCurrentQuestion((c) => c + 1);
                          else setShowResults(true);
                        }}
                        flex={1}
                      >
                        {currentQuestion === megaQuiz.questions.length - 1
                          ? "Finish"
                          : "Next"}
                      </Button>
                    </Group>
                  </>
                ) : (
                  <Stack align="center" justify="center" h={400}>
                    <ThemeIcon
                      size={80}
                      radius="100%"
                      color="green"
                      variant="light"
                    >
                      <IconTrophy size={40} />
                    </ThemeIcon>
                    <Title order={2}>Quiz Done!</Title>
                    <Text size="xl">
                      Score: {getScore()} / {megaQuiz.questions.length}
                    </Text>
                    <Button
                      onClick={() => {
                        setShowResults(false);
                        setCurrentQuestion(0);
                        setSelectedAnswers({});
                      }}
                    >
                      Restart
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Box>
            {/* Notch */}
            <Box className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-b-xl" />
          </Box>
        </Center>
      )}
    </Box>
  );
}
